import {
  endOfDay,
  endOfToday,
  endOfYesterday,
  isAfter,
  startOfDay,
  startOfMonth,
  startOfToday,
  startOfWeek,
  startOfYesterday,
  subDays,
  subHours,
} from "date-fns";

export enum Periods {
  Today = 1,
  Yesterday,
  ThisWeek,
  PastWeek,
  Last7Days,
  ThisMonth,
  Last30Days,
  PastMonth,
}

export const PeriodTypes = [
  { type: Periods.Today, label: "Today" },
  { type: Periods.Yesterday, label: "Yesterday" },
  { type: Periods.ThisWeek, label: "This Week" },
  { type: Periods.PastWeek, label: "Past Week" },
  { type: Periods.Last7Days, label: "Last 7 days" },
  { type: Periods.ThisMonth, label: "This Month" },
  { type: Periods.Last30Days, label: "Last 30 days" },
];

export interface Range {
  start: Date;
  end: Date;
}

export function getWeekStartEnd(dateStr: string) {
  const newStr = dateStr.split("T")[0];
  const startDate = new Date(newStr);

  // set to monday of the week
  const day = startDate.getDay();
  const diff = startDate.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
  startDate.setDate(diff);

  // remove timezone offset
  startDate.setMinutes(startDate.getMinutes() + startDate.getTimezoneOffset());

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 7);
  endDate.setSeconds(endDate.getSeconds() - 1);

  return { startDate, endDate };
}

export function getDayStartEnd(dateStr: string) {
  const newStr = dateStr.split("T")[0];
  const startDate = new Date(newStr);
  startDate.setHours(0, 0, 0, 0);

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 1);
  endDate.setSeconds(endDate.getSeconds() - 1);

  return { startDate, endDate };
}

// remove timezone offset
export function dateToISO(date: Date, ignoreSeconds = false) {
  const newDate = new Date(date.getTime());
  newDate.setMinutes(newDate.getMinutes() - newDate.getTimezoneOffset());
  if (ignoreSeconds) {
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
  }
  return newDate.toISOString()?.split(".")[0];
}

export function dateTexts(language: string) {
  return {
    time: (hour = 13) =>
      Intl.DateTimeFormat(language, {
        hour: "numeric",
        minute: "numeric",
        dayPeriod: "short",
      }).format(new Date(`2022-01-01T${hour}:00`)),
    mondayTime: (hour = 13) =>
      Intl.DateTimeFormat(language, {
        weekday: "long",
        hour: "numeric",
        minute: "numeric",
      }).format(new Date(`2022-01-03T${hour}:00`)),
    weekRange: (
      startDate = new Date("2024-01-01"),
      endDate = new Date("2024-01-07"),
    ) =>
      Intl.DateTimeFormat(language, {
        month: "short",
        day: "numeric",
      }).format(startDate) +
      " - " +
      Intl.DateTimeFormat(language, {
        month: "short",
        day: "numeric",
      }).format(endDate),
  };
}

// add 1 day to end date if it is the smaller than start date
export function adjustEndDate(startDate?: string, endDate?: string) {
  if (!startDate || !endDate) return endDate;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start > end) {
    end.setTime(end.getTime() + 24 * 60 * 60 * 1000);
  }

  return dateToISO(end);
}

export function getWeekNumber(date: Date) {
  const d = new Date(date);
  const dayNumber = (date.getDay() + 6) % 7;
  d.setDate(d.getDate() - dayNumber + 3);
  const firstThursday = d.valueOf();
  d.setMonth(0, 1);
  if (d.getDay() != 4) {
    d.setMonth(0, 1 + ((4 - d.getDay() + 7) % 7));
  }
  return 1 + Math.ceil((firstThursday - d.valueOf()) / 604800000);
}

export const getHoursAndMinutes = (dateTime?: string) => {
  if (!dateTime) return undefined;
  const date = new Date(dateTime);

  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${hours}:${minutes}`;
};

export const calculateShiftDuration = (
  start?: string,
  end?: string,
  breakTime = 0,
) => {
  if (!start || !end) return 0;
  const [startHours, startMinutes] = start.split(":");
  const [endHours, endMinutes] = end.split(":");

  const startMinutesTotal = +startHours * 60 + +startMinutes;
  let endMinutesTotal = +endHours * 60 + +endMinutes;

  if (endMinutesTotal < startMinutesTotal) endMinutesTotal += 24 * 60;

  return endMinutesTotal - startMinutesTotal - breakTime;
};

export const formatDuration = (totalMinutes = 0) => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const formattedHours = hours < 10 ? `0${hours.toFixed(0)}` : hours.toFixed(0);

  return `${formattedHours}h ${minutes < 10 ? `0${minutes.toFixed(0)}` : minutes.toFixed(0)}m`;
};

export function getDateRange(period: Periods, includeToday: boolean): Range {
  const today = startOfToday();
  let start: Date;
  let end: Date;
  switch (period) {
    case Periods.Today:
      start = startOfToday();
      end = includeToday ? endOfToday() : endOfDay(subDays(today, 1));
      break;
    case Periods.Yesterday:
      start = startOfYesterday();
      end = endOfYesterday();
      break;
    case Periods.ThisWeek:
      start = startOfWeek(today, { weekStartsOn: 1 });
      end = includeToday ? endOfToday() : endOfDay(subDays(today, 1));
      break;
    case Periods.PastWeek:
      end = endOfDay(subDays(today, today.getDay()));
      start = startOfDay(subDays(end, 6));
      break;
    case Periods.Last7Days:
      end = includeToday ? endOfToday() : endOfDay(subDays(today, 1));
      start = subDays(end, 6);
      break;
    case Periods.ThisMonth:
      start = startOfMonth(today);
      end = includeToday ? endOfToday() : endOfDay(subDays(today, 1));
      break;
    case Periods.Last30Days:
      end = includeToday ? endOfToday() : endOfDay(subDays(today, 1));
      start = subDays(end, 29);
      break;
    case Periods.PastMonth:
      end = endOfDay(subDays(today, today.getDate()));
      start = startOfDay(subDays(end, end.getDate() - 1));
      break;
  }

  return { start, end };
}

export function getNormalizedStartEnd(
  startStr?: string,
  endStr?: string,
  date = new Date(),
) {
  if (!startStr || !endStr) return [undefined, undefined];

  const start = new Date(date);
  const end = new Date(date);
  if (startStr && endStr) {
    const [startHours, startMinutes] = startStr.split(":").map(Number);

    if (!isNaN(startHours) && !isNaN(startMinutes)) {
      start.setHours(startHours, startMinutes);
    }

    const [endHours, endMinutes] = endStr.split(":").map(Number);

    if (!isNaN(endHours) && !isNaN(endMinutes)) {
      end.setHours(endHours, endMinutes);
      if (end < (start ? start : startStr)) {
        end.setTime(end.getTime() + 24 * 60 * 60 * 1000);
      }
    }
  }
  return [dateToISO(start), dateToISO(end)];
}

export const isWithinLast24Hours = (date?: Date | string | number): boolean => {
  const twentyFourHoursAgo = subHours(new Date(), 24);
  if (date) return isAfter(date, twentyFourHoursAgo);

  return false;
};
